import React from 'react';
import { FileTypeEnum, MediaItemProps } from '@models/media-library.model.ts';
import { Checkbox, IconButton, Tooltip } from '@mui/material';
import {
  DeleteBlackIcon,
  DownloadIcon,
  EditBlackIcon,
  FileUploadIcon,
  MusicIcon,
  PlayIcon,
  PreviewIcon,
  VideoIcon
} from '@components/icons/svg-icon.tsx';
import { formatDate } from '@utils/date-format-utils.ts';
import { toTitleCase } from '@utils/clipboard-utils.ts';
import { getFileType } from '@utils/media-library-utils.ts';
import AppImage from '@components/app-image/app-image.tsx';

const MediaContentItem: React.FC<MediaItemProps> = ({
  data,
  checked,
  disabled,
  isSelected,
  onSelect,
  onCheckedChange,
  onEditClick,
  onDeleteClick,
  onPreviewClick,
  onDownloadClick
}) => {
  const type = getFileType(data);

  const handleCardClick = () => {
    if (!onSelect) return;
    onSelect(isSelected ? null : data);
  };

  const renderIcon = () => {
    switch (type) {
      case FileTypeEnum.Image:
        return (
          <div className="w-full h-full object-cover object-center rounded-[18px]">
            <AppImage
              filePath={data?.path ?? undefined}
              disableModalClick={true}
              rounded="[18px]"
              imgClassName="!h-full"
            />
          </div>
        );
      case FileTypeEnum.Audio:
        return <MusicIcon className="!w-[74px] !h-[74px]" />;
      case FileTypeEnum.Video:
        return <VideoIcon className="!w-[74px] !h-[74px]" />;
      case FileTypeEnum.Document:
      default:
        return <FileUploadIcon className="!w-[74px] !h-[74px]" />;
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`w-[340px] max-w-full h-full max-h-[170px] relative p-3 cursor-pointer border rounded-[10px] transition-colors duration-300 max-lg-xl:w-[300px]
        ${isSelected ? 'border-[var(--primary)] bg-[rgba(248,249,253,1)]' : 'border-[rgba(223,234,242,1)] bg-white'}
      `}
    >
      <div className="absolute left-[2px] top-[1px] z-50">
        <Checkbox
          disabled={disabled}
          className="primary-checkbox"
          sx={{ '& .MuiSvgIcon-root': { height: 20, width: 20 } }}
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
        />
      </div>

      <div className="flex w-full h-full items-center gap-5">
        <div className="flex justify-center min-w-[121px] w-[121px] h-[134px] items-center rounded-[18px] bg-[var(--gray-11)] max-lg-xl:min-w-[90px] max-lg-xl:!w-[90px] max-lg-xl:h-[85px]">
          {renderIcon()}
        </div>

        <div className="py-1 h-full flex flex-col justify-between items-start w-[calc(100%-141px)] max-lg-xl:w-[calc(100%-110px)] max-lg-xl:py-0">
          <div className="flex flex-col gap-1 max-lg-xl:gap-0.5 w-full">
            <Tooltip title={data.name} followCursor>
              <p className="truncate w-full text-base text-[var(--blue-4)] font-medium max-lg-xl:text-[13px]">
                {data.name}
              </p>
            </Tooltip>

            <div className="flex flex-col gap-1.5 max-lg-xl:gap-0.5 w-full">
              <Tooltip
                title={`${data.size} Kb | ${toTitleCase(data.type)}`}
                followCursor
              >
                <p className="text-[13px] text-[rgba(113,142,191,1)] opacity-50 font-normal truncate w-full max-lg-xl:text-[10px]">
                  {data.size} Kb | {toTitleCase(data.type)}
                </p>
              </Tooltip>
              <Tooltip title={formatDate(data.createdAt)} followCursor>
                <p className="text-[13px] text-[rgba(113,142,191,1)] opacity-50 font-normal truncate w-full max-lg-xl:text-[10px]">
                  {formatDate(data.createdAt)}
                </p>
              </Tooltip>
            </div>
          </div>

          <div className="w-full py-0.5 border-t border-0.5 border-dashed border-[rgba(223,234,242,1)]"></div>

          <div className="w-full flex items-center gap-5">
            {type !== FileTypeEnum.Document && (
              <IconButton
                sx={{ border: 'none', backgroundColor: 'var(--primary)' }}
                className="active-icon !w-[30px] !h-[30px] max-lg-xl:!w-[26px] max-lg-xl:!h-[26px]"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewClick?.(data);
                }}
              >
                {type === FileTypeEnum.Image ? (
                  <PreviewIcon className="!w-[17px] !h-[17px]" />
                ) : (
                  <PlayIcon className="!w-[17px] !h-[17px]" />
                )}
              </IconButton>
            )}
            {type === FileTypeEnum.Document && (
              <IconButton
                sx={{
                  border: 'none',
                  backgroundColor: 'var(--gray-11)',
                  width: '30px',
                  height: '30px',
                  '@media (max-width:1715px)': {
                    width: '26px',
                    height: '26px'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadClick?.(data);
                }}
              >
                <DownloadIcon className="w-4.25! h-4.25!" />
              </IconButton>
            )}
            <IconButton
              sx={{
                border: 'none',
                backgroundColor: 'var(--gray-11)',
                width: '30px',
                height: '30px',
                '@media (max-width:1715px)': {
                  width: '26px',
                  height: '26px'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(data);
              }}
            >
              <EditBlackIcon className="w-4.25! h-4.25!" />
            </IconButton>
            <IconButton
              sx={{
                border: 'none',
                backgroundColor: 'var(--gray-11)',
                width: '30px',
                height: '30px',
                '@media (max-width:1715px)': {
                  width: '26px',
                  height: '26px'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(data);
              }}
            >
              <DeleteBlackIcon className="w-4.25! h-4.25!" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaContentItem;
